import React, { useRef, useState } from 'react';
import { FileText, Upload, X, Loader2, AlertCircle } from 'lucide-react';
import type { FoodCourtMenuMetadata } from './menuTypes';
import { uploadFoodCourtMenu } from './menuUploadClient';

type MenuUploadFieldProps = {
  locationSlug: string;
  itemKey: string;
  /** Existing food court content id — menu is saved to the item on upload. */
  contentId?: string;
  value: FoodCourtMenuMetadata | null;
  onChange: (menu: FoodCourtMenuMetadata | null) => void;
  disabled?: boolean;
};

export function MenuUploadField({
  locationSlug,
  itemKey,
  contentId,
  value,
  onChange,
  disabled,
}: MenuUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file || disabled) return;
    setError(null);
    setUploading(true);
    setProgress(0);
    try {
      const menu = await uploadFoodCourtMenu({
        file,
        locationSlug,
        itemKey,
        contentId,
        setUploadProgressPercent: setProgress,
      });
      onChange(menu);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const displayLabel =
    value?.fileName || value?.key?.split('/').pop() || (value?.url ? 'Menu file' : null);

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-foreground">Menu (PDF or image)</label>
      <p className="text-xs text-muted-foreground">
        Admin only. Saved on the server at{' '}
        <code className="text-xs bg-muted px-1 rounded">
          uploads/food-court/{locationSlug}/food-court/menu-…
        </code>
        . Max 5MB.
        {contentId && ' Uploading here saves the menu on this item immediately.'}
      </p>

      {displayLabel && (
        <div className="flex items-center gap-2 text-sm text-foreground bg-muted/50 border rounded-lg px-3 py-2">
          <FileText className="w-4 h-4 shrink-0 text-primary" />
          <span className="truncate flex-1">{displayLabel}</span>
          {!disabled && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-muted-foreground hover:text-destructive"
              title="Remove menu"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      <div className="flex items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          className="hidden"
          disabled={disabled || uploading}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <button
          type="button"
          disabled={disabled || uploading || !locationSlug || !itemKey}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 px-4 py-2 border border-input rounded-lg text-sm font-medium hover:bg-muted disabled:opacity-50"
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          {value ? 'Replace menu' : 'Upload menu'}
        </button>
        {uploading && (
          <span className="text-sm text-muted-foreground">{progress}%</span>
        )}
      </div>

      {error && (
        <p className="text-sm text-destructive flex items-center gap-1">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}
